var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/**
 * [CLAIMS_CONTEXT]
 * purpose: Provides the purchase information step for personal property damage
 * declaration. Allows the user to specify whether they know the purchase price
 * and purchase date, enter age and amount information, or confirm purchase
 * certification when price information is unavailable.
 * touches: personal property purchase flow, damage declaration form,
 * personal property Vuex store, validation handling, amount and age inputs,
 * purchase certification logic and third-party product information behavior.
 * invariants: personal property data is always updated through the damage store
 * updateDamage action; amount and age availability depends on userKnowPrice,
 * identity document status, amount-only mode and third-party product data;
 * purchase certification is mutually exclusive with known price information.
 * inputs: personal property resource payload, changeKey identifying the current
 * damage entity, validation context, third-party product information, amount,
 * age, user price knowledge state, purchase certification state and feature
 * specific personal property rules.
 * outputs: updates personal property fields through Vuex actions; emits changes
 * for userKnowPrice, amount, age and purchaseCertification; renders dynamic
 * purchase-related fields with validation messages and translated labels.
 * see: frontend/src/declaration/components/personalProperty/PersonalPropertyPurchase.vue,
 * frontend/src/declaration/components/UI/AmountInput.vue,
 * frontend/src/declaration/components/UI/AgeInput.vue,
 * frontend/src/declaration/store/personalProperty.ts,
 * frontend/src/declaration/store/createDamageStore.ts,
 * frontend/src/shared/types/api/personalProperty.ts,
 * frontend/src/shared/types/api/claim.ts
 */
import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { UiSwitch, UiCheckbox } from '@claims-ia/design-system';
import AmountInput from '@declaration/components/UI/AmountInput.vue';
import AgeInput from '@declaration/components/UI/AgeInput.vue';
const personalProperty = namespace('personalProperty');
let PersonalPropertyPurchase = class PersonalPropertyPurchase extends Vue {
    get purchaseCertificationChoice() {
        return [
            {
                value: 'purchaseCertification',
                label: this.purchaseCertificationLabel,
            },
        ];
    }
    onPurchaseCertificationChanged(value) {
        const checked = value.includes('purchaseCertification');
        this.update({
            purchaseCertification: checked,
            userKnowPrice: checked ? false : null,
            amount: checked ? null : this.amount,
            age: checked ? null : this.age,
        });
    }
    get amountLabel() {
        if (this.isAmountOnly)
            return 'Montant';
        return "Prix d'achat";
    }
    get userKnowPriceLabel() {
        var _a;
        if ((_a = this.thirdPartyProductInfo) === null || _a === void 0 ? void 0 : _a.amount)
            return "Je connais la date d'achat du bien";
        return "Je connais le prix et la date d'achat du bien";
    }
    get purchaseCertificationLabel() {
        var _a;
        if ((_a = this.thirdPartyProductInfo) === null || _a === void 0 ? void 0 : _a.amount)
            return this.$tc('claim.damage.personal_property.form.question.user_purchase_certification.without_price');
        return this.$tc('claim.damage.personal_property.form.question.user_purchase_certification');
    }
    get change() {
        return this.changeGetter(this.changeKey);
    }
    get validation() {
        return this.validationGetter(this.changeKey);
    }
    get showErrors() {
        var _a;
        return (_a = this.validation.validator) === null || _a === void 0 ? void 0 : _a.revealViolations;
    }
    get personalProperty() {
        return this.change.data;
    }
    get thirdPartyProductInfo() {
        var _a;
        return (_a = this.personalProperty.thirdPartyProductInfo) !== null && _a !== void 0 ? _a : null;
    }
    get isAmountOnly() {
        return this.isAmountOnlyGetter(this.changeKey);
    }
    get isIdentityDocument() {
        return this.isIdentityDocumentGetter(this.changeKey);
    }
    get isUserKnowPriceModifiable() {
        return !this.isAmountOnly && !this.isIdentityDocument;
    }
    get isAmountModifiable() {
        var _a;
        if (this.isAmountOnly)
            return true;
        if (this.isIdentityDocument)
            return false;
        if ((_a = this.thirdPartyProductInfo) === null || _a === void 0 ? void 0 : _a.amount)
            return false;
        if (this.userKnowPrice !== true)
            return false;
        return true;
    }
    get isAgeModifiable() {
        if (this.isAmountOnly || this.isIdentityDocument)
            return false;
        if (this.personalProperty.userKnowPrice === false)
            return false;
        if (this.userKnowPrice !== true)
            return false;
        return true;
    }
    get isCertificationModifiable() {
        return !this.userKnowPrice && !this.isAmountOnly;
    }
    get userKnowPrice() {
        var _a;
        return (_a = this.personalProperty.userKnowPrice) !== null && _a !== void 0 ? _a : null;
    }
    handleUserNoPriceInput(userKnowPrice) {
        this.update({
            userKnowPrice,
            amount: userKnowPrice ? this.amount : null,
            age: userKnowPrice ? this.age : null,
            receipt: userKnowPrice ? this.personalProperty.receipt : null,
        });
    }
    get age() {
        var _a;
        return (_a = this.personalProperty.age) !== null && _a !== void 0 ? _a : null;
    }
    set age(age) {
        this.update({ age });
    }
    get amount() {
        return typeof this.personalProperty.amount === 'number' ? this.personalProperty.amount : null;
    }
    set amount(amount) {
        this.update({ amount: typeof amount === 'number' ? amount : null });
    }
    get purchaseCertification() {
        var _a;
        return (_a = this.personalProperty.purchaseCertification) !== null && _a !== void 0 ? _a : null;
    }
    set purchaseCertification(purchaseCertification) {
        this.update({ purchaseCertification });
    }
    fieldType(key) {
        var _a;
        return ((_a = this.validation.validator) === null || _a === void 0 ? void 0 : _a.revealViolations) && !this.validation.fieldIsValid(key)
            ? 'is-danger'
            : null;
    }
    fieldMessage(key) {
        var _a;
        return ((_a = this.validation.validator) === null || _a === void 0 ? void 0 : _a.revealViolations)
            ? this.validation.fieldErrors(key, this)[0]
            : null;
    }
    update(data) {
        this.updatePersonalProperty({ changeKey: this.changeKey, data });
    }
    onPurchaseCertificationChange(newVal) {
        if (newVal === true) {
            this.update({ userKnowPrice: false });
        }
    }
};
__decorate([
    personalProperty.Getter('change')
], PersonalPropertyPurchase.prototype, "changeGetter", void 0);
__decorate([
    personalProperty.Getter('validation')
], PersonalPropertyPurchase.prototype, "validationGetter", void 0);
__decorate([
    personalProperty.Getter('isAmountOnly')
], PersonalPropertyPurchase.prototype, "isAmountOnlyGetter", void 0);
__decorate([
    personalProperty.Getter('isIdentityDocument')
], PersonalPropertyPurchase.prototype, "isIdentityDocumentGetter", void 0);
__decorate([
    personalProperty.Action('updateDamage')
], PersonalPropertyPurchase.prototype, "updatePersonalProperty", void 0);
__decorate([
    Watch('purchaseCertification')
], PersonalPropertyPurchase.prototype, "onPurchaseCertificationChange", null);
PersonalPropertyPurchase = __decorate([
    Component({
        components: { AmountInput, AgeInput, UiSwitch, UiCheckbox },
        props: { changeKey: { type: [String, Number], default: null } },
    })
], PersonalPropertyPurchase);
export default PersonalPropertyPurchase;
